// Business statuses are OWNED BY THE BACKEND. Never invent new ones here.
export const COMMANDE_STATUTS = [
  "NOUVEAU",
  "A_VERIFIER",
  "APPROUVE",
  "RESERVE",
  "A_ACHETER",
  "EN_PREPARATION",
  "PRET",
  "EXPEDIE",
  "FACTURE",
  "FACTURE_ENVOYEE",
  "FERME",
  "ANNULE",
] as const;

export type CommandeStatut = (typeof COMMANDE_STATUTS)[number];

export type StatusTone = "success" | "attention" | "critical" | "info" | "neutral";

export const STATUT_TONE: Record<CommandeStatut, StatusTone> = {
  NOUVEAU: "info",
  A_VERIFIER: "attention",
  APPROUVE: "success",
  RESERVE: "neutral",
  A_ACHETER: "attention",
  EN_PREPARATION: "info",
  PRET: "success",
  EXPEDIE: "success",
  FACTURE: "info",
  FACTURE_ENVOYEE: "info",
  FERME: "neutral",
  ANNULE: "critical",
};

export type Severite = "critical" | "attention" | "info";

// Contrat exact de GET /dashboard/resume (10 KPI) tel que défini par le backend.
// Les noms de champs sont normatifs ; aucune valeur n'est hardcodée côté frontend.
export interface DashboardResume {
  po_recus_aujourdhui: number;
  commandes_ouvertes: number;
  commandes_a_verifier: number;
  commandes_a_acheter: number;
  commandes_a_preparer: number;
  expediees_a_facturer: number;
  factures_a_envoyer: number;
  produits_stock_faible: number;
  valeur_commandes_ouvertes: number;
  valeur_attente_fournisseur: number;
}

export type DashboardResumeKey = keyof DashboardResume;

export const DASHBOARD_RESUME_KEYS: DashboardResumeKey[] = [
  "po_recus_aujourdhui",
  "commandes_ouvertes",
  "commandes_a_verifier",
  "commandes_a_acheter",
  "commandes_a_preparer",
  "expediees_a_facturer",
  "factures_a_envoyer",
  "produits_stock_faible",
  "valeur_commandes_ouvertes",
  "valeur_attente_fournisseur",
];

// Champs monétaires : formatés en devise à l'affichage.
export const DASHBOARD_RESUME_CURRENCY_KEYS: DashboardResumeKey[] = [
  "valeur_commandes_ouvertes",
  "valeur_attente_fournisseur",
];

export interface DashboardAlerte {
  code: string;
  message: string;
  severite: Severite;
  reference?: string | null;
}

export interface CommandeOuverte {
  id: string;
  numero_commande: string;
  numero_po_client: string | null;
  date_reception: string;
  statut: CommandeStatut;
  valeur_commande: number;
  client: {
    id: string;
    nom: string;
  };
}

export interface Commande {
  id: string;
  numero_commande: string;
  numero_po_client: string;
  date_reception: string;
  statut: CommandeStatut;
  valeur_commande: number | null;
  numero_commande_sage: string | null;
  numero_facture_sage: string | null;
  facture_creee: boolean;
  facture_envoyee: boolean;
  client: {
    id: string;
    nom: string;
  };
}

export interface LigneCommande {
  id: string;
  produit_id: string;
  sku: string;
  description: string;
  quantite_demandee: number;
  prix_unitaire_po: number;
  prix_unitaire_officiel: number | null;
  quantite_disponible: number;
  quantite_reservee: number;
  quantite_a_acheter: number;
  quantite_expediee: number;
  statut: string;
  besoin_achat: {
    id: string;
    statut: string;
    quantite_a_acheter: number;
  } | null;
}

export interface CommandeDetail {
  id: string;
  numero_commande: string;
  client: {
    id: string;
    nom_entreprise: string;
  };
  date_reception: string;
  numero_po_client: string;
  courriel_acheteur: string | null;
  statut: CommandeStatut;
  valeur_commande: number | null;
  approbateur: string | null;
  numero_commande_sage: string | null;
  date_expedition: string | null;
  preuve_expedition: string | null;
  numero_facture_sage: string | null;
  facture_creee: boolean;
  facture_envoyee: boolean;
  date_envoi_facture: string | null;
  erreur_exception: string | null;
  cree_le: string;
  modifie_le: string;
  lignes: LigneCommande[];
}

export interface LigneCommandeCreatePayload {
  sku: string;
  quantite: number;
  prix_unitaire_po: number;
}

export interface CommandeCreatePayload {
  client_id: string;
  numero_po_client: string;
  courriel_acheteur?: string | null;
  lignes: LigneCommandeCreatePayload[];
}

export interface CommandeCreateResponse {
  id: string;
  numero_commande: string;
  statut: CommandeStatut;
  valeur_commande: number;
  message: string;
}

export interface CommandeReservationPayload {
  idempotency_key: string;
  commentaire?: string | null;
}

export interface CommandeReservationResponse {
  commande_id: string;
  numero_commande: string;
  quantite_reservee: number;
  quantite_a_acheter: number;
  besoins_achat: Array<{
    id: string;
    ligne_commande_id: string;
    produit_id: string;
    sku: string;
    quantite_a_acheter: number;
    statut: string;
  }>;
  statut: "RESERVE" | "A_ACHETER";
  message: string;
}

export interface BesoinAchat {
  id: string;
  commande: {
    id: string;
    numero_commande: string;
  };
  ligne_commande_id: string;
  produit: {
    id: string;
    sku: string;
    description: string;
  };
  fournisseur: {
    id: string;
    nom: string;
  } | null;
  quantite_a_acheter: number;
  quantite_commandee: number;
  quantite_recue: number;
  statut: string;
  numero_commande_fournisseur: string | null;
  date_commande: string | null;
  date_reception_complete: string | null;
  commentaire: string | null;
  cree_le: string;
  modifie_le: string;
  fournisseurs_compatibles: Array<{
    id: string;
    nom: string;
    code_fournisseur: string | null;
    reference_fournisseur: string | null;
  }>;
}

export interface CommandeFournisseurLignePayload {
  besoin_achat_id: string;
  quantite_commandee: number;
}

export interface CommandeFournisseurCreatePayload {
  fournisseur_id: string;
  numero_commande_fournisseur: string;
  lignes: CommandeFournisseurLignePayload[];
  commentaire?: string | null;
}

export interface CommandeFournisseur {
  id: string;
  numero_commande_fournisseur: string;
  fournisseur: { id: string; nom: string };
  statut: string;
  date_commande: string;
  commentaire: string | null;
  cree_le: string;
  modifie_le: string;
  nombre_lignes: number;
  quantite_commandee: number;
}

export interface CommandeFournisseurCreateResponse {
  id: string;
  numero_commande_fournisseur: string;
  fournisseur_id: string;
  statut: string;
  date_commande: string;
  commentaire: string | null;
  lignes: Array<{
    besoin_achat_id: string;
    produit_id: string;
    quantite_commandee: number;
    reference_fournisseur: string | null;
  }>;
  idempotent_replay: boolean;
}

export interface CommandeFournisseurDetail {
  id: string;
  numero_commande_fournisseur: string;
  fournisseur: { id: string; nom: string };
  statut: string;
  date_commande: string;
  commentaire: string | null;
  cree_le: string;
  modifie_le: string;
  lignes: Array<{
    id: string;
    besoin_achat_id: string;
    produit: { id: string; sku: string; description: string };
    quantite_commandee: number;
    quantite_recue: number;
    quantite_restante: number;
    statut: string;
    reference_fournisseur: string | null;
    commande_client: {
      id: string;
      numero_commande: string;
      ligne_commande_id: string;
    };
    receptions: Array<{
      id: string;
      quantite_recue: number;
      numero_reception: string | null;
      utilisateur: string | null;
      cree_le: string;
    }>;
  }>;
}

export interface ReceptionFournisseurPayload {
  idempotency_key: string;
  quantite_recue: number;
  numero_reception?: string | null;
  preuve?: string | null;
  commentaire?: string | null;
}

export interface ReceptionFournisseurResponse {
  reception_id: string;
  commande_fournisseur_id: string;
  ligne_commande_fournisseur_id: string;
  besoin_achat_id: string;
  produit_id: string;
  fournisseur_id: string;
  numero_commande_fournisseur: string;
  commande_client_id: string;
  quantite_recue: number;
  quantite_recue_totale: number;
  quantite_restante: number;
  statut_ligne: string;
  statut_commande: string;
  stock_physique: number;
  quantite_en_commande: number;
  idempotent_replay: boolean;
  message: string;
}

export interface CommandeEligiblePreparation {
  commande_id: string;
  numero_commande: string;
  numero_po_client: string;
  date_reception: string;
  client: string;
  nombre_lignes: number;
  quantite_a_preparer: number;
}

export interface PreparationEnCours {
  preparation_id: string;
  commande_id: string;
  numero_commande: string;
  numero_po_client: string;
  client: string;
  quantite_attendue: number;
  quantite_preparee: number;
  commence_le: string;
}

export interface LignePreparation {
  id: string;
  ligne_commande_id: string;
  produit_id: string;
  sku: string;
  description: string;
  emplacement: string | null;
  quantite_attendue: number;
  quantite_preparee: number;
  quantite_confirmee: number;
  ecart: number;
}

export interface PreparationDetail {
  id: string;
  commande_id: string;
  statut: "EN_COURS" | "FINALISEE";
  prepare_par: string | null;
  finalise_par: string | null;
  commence_le: string;
  finalise_le: string | null;
  commande: {
    numero_commande: string;
    numero_po_client: string;
    statut: CommandeStatut;
    client: string;
  };
  progression: {
    quantite_attendue: number;
    quantite_preparee: number;
    complete: boolean;
  };
  lignes: LignePreparation[];
  idempotent_replay?: boolean;
}

export interface ScanPreparationPayload {
  ligne_preparation_id: string;
  identifiant: string;
  quantite: number;
  idempotency_key: string;
}

export interface ScanPreparationResponse {
  scan_id: string;
  preparation_id: string;
  ligne_preparation_id: string;
  produit_id: string;
  quantite: number;
  idempotent_replay: boolean;
  preparation: PreparationDetail;
}

export interface CommandePreteExpedition {
  commande_id: string;
  numero_commande: string;
  numero_po_client: string;
  client: string;
  nombre_lignes_restantes: number;
  quantite_restante: number;
  brouillon_id: string | null;
}

export interface LigneExpedition {
  id: string;
  ligne_commande_id: string;
  produit_id: string;
  sku: string;
  description: string;
  quantite_prevue: number;
  quantite_expediee: number;
  quantite_commandee: number;
  quantite_expediee_cumulative: number;
  quantite_restante: number;
  mouvement_stock_id: string | null;
}

export interface ExpeditionDetail {
  id: string;
  commande_id: string;
  statut: "BROUILLON" | "CONFIRMEE";
  expedie_par: string | null;
  preuve_depart: string | null;
  commentaire: string | null;
  cree_le: string;
  confirme_le: string | null;
  commande: {
    numero_commande: string;
    numero_po_client: string;
    statut: CommandeStatut;
    client: string;
  };
  quantite_prevue: number;
  quantite_expediee: number;
  lignes: LigneExpedition[];
  idempotent_replay?: boolean;
}

export interface CreationExpeditionPayload {
  idempotency_key: string;
  lignes: Array<{ ligne_commande_id: string; quantite: number }>;
}

export interface ConfirmationExpeditionPayload {
  idempotency_key: string;
  preuve_depart?: string | null;
  commentaire?: string | null;
}

export type PreuveLivraisonStatut =
  | "RECU" | "ANALYSE" | "IDENTIFIE" | "A_VALIDER"
  | "LIVRAISON_VALIDEE" | "NON_IDENTIFIE" | "AMBIGU"
  | "DOCUMENT_ILLISIBLE" | "SIGNATURE_NON_CONFIRMEE"
  | "DONNEES_INCOHERENTES" | "DOUBLON" | "ERREUR_TRAITEMENT" | "REFUSEE";

export interface PreuveLivraison {
  id: string;
  expedition_id: string | null;
  commande_id: string | null;
  source: "POWER_AUTOMATE" | "IMPORT_MANUEL" | "E2E";
  reference_externe: string | null;
  reference_sharepoint: string | null;
  nom_fichier: string;
  sha256: string;
  mime_type: string;
  taille_octets: number;
  recu_le: string;
  correlation_id: string | null;
  statut: PreuveLivraisonStatut;
  niveau_confiance: "INDETERMINE" | "FAIBLE" | "MOYEN" | "ELEVE";
  score_confiance: number;
  document_signe: "PROBABLEMENT_SIGNE" | "PROBABLEMENT_NON_SIGNE" | "INDETERMINE";
  resultat_analyse: {
    identification?: string;
    decision_proposee?: string;
    mode_observation?: boolean;
    auto_validation_executee?: boolean;
    candidats?: string[];
  };
  raisons: string[];
  livraison_confirmee: boolean;
  validation_mode: "AUTOMATIQUE" | "HUMAINE" | null;
  valide_le: string | null;
  exception_id: string | null;
  metadata: Record<string, unknown>;
  commande: {
    numero_commande: string;
    numero_po_client: string | null;
    client: string;
  } | null;
  expedition_confirmee_le: string | null;
  idempotent_replay?: boolean;
}

export interface AssociationPreuveLivraisonPayload {
  idempotency_key: string;
  expedition_id: string;
  motif: string;
}

export interface DecisionPreuveLivraisonPayload {
  idempotency_key: string;
  motif?: string | null;
}

export interface LigneFacturableExpedition {
  expedition_id: string;
  expedition_confirmee_le: string;
  ligne_expedition_id: string;
  ligne_commande_id: string;
  produit_id: string;
  sku: string;
  description: string;
  quantite_expediee: number;
  quantite_deja_facturee: number;
  quantite_restante: number;
  prix_unitaire_po: number;
  prix_unitaire_officiel: number | null;
  source_prix: "PO_CLIENT_VALIDE";
}

export interface CommandeFacturable {
  commande_id: string;
  numero_commande: string;
  numero_po_client: string;
  client: string;
  devise: string;
  condition_paiement_jours: number | null;
  expedition_id: string;
  expedition_confirmee_le: string;
  brouillon_id: string | null;
  quantite_restante: number;
  lignes: LigneFacturableExpedition[];
}

export interface LigneFacture {
  id: string;
  ligne_expedition_id: string;
  expedition_id: string;
  ligne_commande_id: string;
  produit_id: string;
  sku: string;
  description: string;
  quantite_facturee: number;
  quantite_expediee_source: number;
  prix_unitaire_po: number;
  prix_unitaire_officiel: number | null;
  prix_unitaire_facture: number;
  source_prix: "PO_CLIENT_VALIDE";
  sous_total: number;
}

export interface FactureDetail {
  id: string;
  commande_id: string;
  statut: "BROUILLON" | "CONFIRMEE" | "SAGE_ASSOCIEE";
  commande: {
    numero_commande: string;
    numero_po_client: string;
    client: string;
  };
  numero_facture_sage: string | null;
  numero_commande_sage: string | null;
  devise: string;
  condition_paiement_jours: number | null;
  sous_total: number;
  montant_taxes: null;
  total: null;
  politique_taxes: "NON_CALCULEE";
  preparee_par: string | null;
  confirmee_par: string | null;
  commentaire: string | null;
  cree_le: string;
  confirme_le: string | null;
  associe_sage_le: string | null;
  modifie_le: string;
  expedition_ids: string[];
  quantite_facturee: number;
  lignes: LigneFacture[];
  idempotent_replay?: boolean;
}

export interface CreationFacturePayload {
  idempotency_key: string;
  lignes: Array<{ ligne_expedition_id: string; quantite: number }>;
  commentaire?: string | null;
}

export interface ConfirmationFacturePayload {
  idempotency_key: string;
  commentaire?: string | null;
}

export interface AssociationSagePayload {
  idempotency_key: string;
  numero_facture_sage: string;
  numero_commande_sage?: string | null;
  commentaire?: string | null;
}

export interface DashboardAlerteCommande {
  id: string;
  numero_commande: string;
  numero_po_client: string | null;
  statut: string;
  valeur_commande: number;
  erreur_exception: string | null;
}

export interface DashboardAlerteStock {
  id: string;
  sku: string;
  description: string;
  quantite_physique: number;
  quantite_reservee: number;
  quantite_disponible: number;
  quantite_en_commande: number;
  stock_minimum: number;
  emplacement: string | null;
}

export interface DashboardAlertesResponse {
  commandes: DashboardAlerteCommande[];
  stock: DashboardAlerteStock[];
}
export interface Client {
  id: string;
  code_client: string;
  numero_client_sage: string | null;
  nom_legal: string;
  nom_commercial: string | null;
  type_client: string;
  statut: string;
  courriel_general: string | null;
  telephone_general: string | null;
  langue: string;
  devise: string;
}
export interface ClientContact {
  id: string;
  client_id: string;
  prenom: string;
  nom: string;
  titre_poste: string | null;
  departement: string | null;
  courriel: string | null;
  telephone: string | null;
  poste_telephonique: string | null;
  cellulaire: string | null;
  contact_principal: boolean;
  contact_facturation: boolean;
  contact_achats: boolean;
  contact_expedition: boolean;
  actif: boolean;
  notes: string | null;
}

export interface ClientAdresse {
  id: string;
  client_id: string;
  type_adresse: string;
  nom_adresse: string;
  adresse_ligne_1: string;
  adresse_ligne_2: string | null;
  ville: string;
  province_etat: string;
  code_postal: string;
  pays: string;
  principale: boolean;
  active: boolean;
  instructions_livraison: string | null;
}
export interface ClientActivite {
  id: string;
  client_id: string;
  contact_id: string | null;
  type_activite: string;
  sujet: string;
  description: string | null;
  date_activite: string;
  date_echeance: string | null;
  statut: string;
  responsable_id: string | null;
  cree_le: string;
  modifie_le: string;
}

export interface ClientNote {
  id: string;
  client_id: string;
  titre: string;
  contenu: string;
  type_note: string;
  confidentielle: boolean;
  cree_par: string | null;
  cree_le: string;
  modifie_le: string;
}

export interface StockSummary {
  quantite_physique: number;
  quantite_reservee: number;
  quantite_disponible: number;
  quantite_en_commande: number;
  stock_projete: number;
  stock_minimum: number;
  emplacement: string | null;
}

export interface Product extends StockSummary {
  id: string;
  sku: string;
  description_officielle: string;
  famille: string | null;
  unite_mesure: string;
  cout_reference: number | null;
  actif: boolean;
}

export const PRODUCT_IDENTIFIER_TYPES = [
  "SKU_ROCKFALL",
  "UPC",
  "EAN",
  "GTIN",
  "REFERENCE_FOURNISSEUR",
  "CODE_INTERNE",
] as const;

export type ProductIdentifierType = (typeof PRODUCT_IDENTIFIER_TYPES)[number];

export interface ProductIdentifierResolution {
  code_recu: string;
  code_normalise: string;
  type_identifiant: ProductIdentifierType;
  produit_id: string;
  sku_rockfall: string;
  description: string;
  emplacement: string | null;
  quantite_physique: number;
  quantite_reservee: number;
  quantite_disponible: number;
  quantite_en_commande: number;
  statut: "ACTIF";
}

export type DocumentResolutionStatus =
  | "RESOLU_SKU"
  | "RESOLU_RF_CODE"
  | "RESOLU_ALIAS_CLIENT"
  | "RESOLU_ALIAS_GLOBAL"
  | "REFERENCE_INCONNUE"
  | "REFERENCE_AMBIGUE";

export interface DocumentResolutionCandidate {
  produit_id: string;
  sku_rockfall: string;
  description: string;
  configuration_id: string | null;
  rf_code: string | null;
  statut_configuration: "ACTIF" | "INACTIF" | null;
  provenance_principale: string;
  type_resolution_snapshot: string;
}

export interface DocumentResolutionResult {
  reference_document: string;
  reference_normalisee: string;
  statut_resolution: DocumentResolutionStatus;
  nombre_candidats: number;
  produit_id: string | null;
  sku_rockfall: string | null;
  configuration_id: string | null;
  rf_code: string | null;
  statut_configuration: "ACTIF" | "INACTIF" | null;
  contexte_resolution: string | null;
  type_resolution_snapshot: string | null;
  candidats: DocumentResolutionCandidate[];
}

export interface Supplier {
  id: string;
  nom: string;
  code_fournisseur: string | null;
  courriel: string | null;
  telephone: string | null;
  actif: boolean;
  cree_le: string;
  modifie_le: string;
}

export interface ProductPriceTier {
  quantite_minimum: number;
  quantite_maximum: number | null;
  prix_unitaire: number;
  actif: boolean;
}

export interface ProductSupplier {
  id: string;
  code_fournisseur: string | null;
  nom: string;
  reference_fournisseur: string | null;
  cout_fournisseur: number | null;
  delai_jours: number | null;
  fournisseur_principal: boolean;
  actif: boolean;
}

export interface ProductDetail extends Product {
  prix: ProductPriceTier[];
  fournisseurs: ProductSupplier[];
}

export interface ProductPrice {
  produit_id: string;
  quantite: number;
  prix_unitaire: number;
  palier_quantite_minimum: number;
  palier_quantite_maximum: number | null;
}

export interface StockMovement {
  id: string;
  produit_id: string;
  type_mouvement: string;
  quantite: number;
  reference_type: string | null;
  reference_id: string | null;
  utilisateur: string | null;
  preuve: string | null;
  commentaire: string | null;
  cree_le: string;
}
